import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IRACING_AUTH_API_BASE } from "../utils/iracingAuthApi";
import { notifyIracingLinkChanged } from "../utils/driverPrivacy";
import "../styles/IracingLinkOrCreate.css";

const IracingLinkOrCreate = ({ setIsLoggedIn }) => {
        const navigate = useNavigate();

        // Capture URL params once at first render so React 18 Strict Mode's
        // double-invoked useEffect doesn't see an empty search string after
        // we've navigated away.
        const [parsed] = useState(() => {
                const sp = new URLSearchParams(window.location.search);
                return {
                        tempToken: sp.get("tempToken") || "",
                        displayName: sp.get("displayName") || "",
                };
        });
        const tempToken = parsed.tempToken;
        const displayName = parsed.displayName;

        // Whether the "link to existing account" form is expanded.
        const [showLinkForm, setShowLinkForm] = useState(false);

        // Link-form fields
        const [username, setUsername] = useState("");
        const [password, setPassword] = useState("");

        const [submitting, setSubmitting] = useState(false);
        const [errorMsg, setErrorMsg] = useState("");

        useEffect(() => {
                if (!tempToken) {
                        // eslint-disable-next-line no-console
                        console.error("IracingLinkOrCreate missing tempToken");
                        navigate("/iracing-auth-error?error=missing_token&from=link", { replace: true });
                }
        }, [navigate, tempToken]);

        const finishLogin = (data) => {
                localStorage.setItem("token", data.token);
                localStorage.setItem("username", data.username);
                localStorage.setItem("isPaidUser", data.isPaidUser ? "true" : "false");
                localStorage.setItem("iracingLinked", "true");
                localStorage.setItem("iracingOnly", data.iracingOnly ? "true" : "false");
                notifyIracingLinkChanged();
                if (typeof setIsLoggedIn === "function") {
                        setIsLoggedIn(true);
                }
                navigate("/", { replace: true });
        };

        const handleContinue = async () => {
                setErrorMsg("");
                setSubmitting(true);
                try {
                        const response = await fetch(`${IRACING_AUTH_API_BASE}/api/auth/iracing/complete-iracing-only`, {
                                method: "POST",
                                headers: { "Content-Type": "application/json" },
                                body: JSON.stringify({ tempToken }),
                        });
                        const data = await response.json();
                        if (response.ok) {
                                finishLogin(data);
                        } else {
                                setErrorMsg(data.message || "Failed to sign in.");
                        }
                } catch (err) {
                        console.error("complete-iracing-only error:", err);
                        setErrorMsg("An error occurred. Please try again.");
                } finally {
                        setSubmitting(false);
                }
        };

        const handleLinkSubmit = async (e) => {
                e.preventDefault();
                setErrorMsg("");

                if (!username || !password) {
                        setErrorMsg("Please enter your existing email and password.");
                        return;
                }

                setSubmitting(true);
                try {
                        const response = await fetch(`${IRACING_AUTH_API_BASE}/api/auth/iracing/complete-link`, {
                                method: "POST",
                                headers: { "Content-Type": "application/json" },
                                body: JSON.stringify({ tempToken, username, password }),
                        });
                        const data = await response.json();
                        if (response.ok) {
                                finishLogin(data);
                        } else {
                                setErrorMsg(data.message || "Failed to link account.");
                        }
                } catch (err) {
                        console.error("complete-link error:", err);
                        setErrorMsg("An error occurred. Please try again.");
                } finally {
                        setSubmitting(false);
                }
        };

        return (
                <div className="iracing-link-page">
                        <div className="iracing-link-card">
                                <h2>Welcome{displayName ? `, ${displayName}` : ""}</h2>
                                <p className="iracing-link-subtitle">
                                        Your iRacing account has been verified. Click Continue to finish signing in.
                                </p>

                                <button
                                        type="button"
                                        className="iracing-link-primary-btn"
                                        onClick={handleContinue}
                                        disabled={submitting}
                                >
                                        {submitting && !showLinkForm ? "Signing in…" : "Continue"}
                                </button>

                                {!showLinkForm ? (
                                        <button
                                                type="button"
                                                className="iracing-link-secondary-link"
                                                onClick={() => { setShowLinkForm(true); setErrorMsg(""); }}
                                        >
                                                Already have an iRacingData account? Link it
                                        </button>
                                ) : (
                                        <div className="iracing-link-form-wrapper">
                                                <div className="iracing-link-divider">Link existing account</div>
                                                <form className="iracing-link-form" onSubmit={handleLinkSubmit}>
                                                        <input
                                                                type="email"
                                                                placeholder="Email"
                                                                value={username}
                                                                onChange={(e) => setUsername(e.target.value)}
                                                        />
                                                        <input
                                                                type="password"
                                                                placeholder="Password"
                                                                value={password}
                                                                onChange={(e) => setPassword(e.target.value)}
                                                        />
                                                        <button type="submit" disabled={submitting}>
                                                                {submitting ? "Linking…" : "Link Account"}
                                                        </button>
                                                </form>
                                                <button
                                                        type="button"
                                                        className="iracing-link-secondary-link"
                                                        onClick={() => { setShowLinkForm(false); setErrorMsg(""); }}
                                                >
                                                        Cancel
                                                </button>
                                        </div>
                                )}

                                {errorMsg && <div className="iracing-link-error">{errorMsg}</div>}
                        </div>
                </div>
        );
};

export default IracingLinkOrCreate;
